import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Alert,
  Container,
  Form,
  Input,
  Label,
  FormFeedback,
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import logo from '../../assets/images/NGK-LOGO.png';
import CarouselPage from '../AuthenticationInner/CarouselPage';
import {
  clearPendingEmailOtp,
  getPendingEmailOtp,
} from '../../helpers/wordpress_auth';
import { resendEmailOtp, verifyEmailOtp } from '../../helpers/wordpress_email_otp';
import { storePendingTotp } from '../../helpers/wordpress_totp';
import { loginSuccess } from '../../store/actions';

const EmailOtp = () => {
  document.title = 'Email Verification | NGK Tours';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [emailHint, setEmailHint] = useState('your email');

  const pending = useMemo(() => getPendingEmailOtp(), []);

  useEffect(() => {
    if (!pending?.challengeToken) {
      navigate('/login', { replace: true });
      return;
    }
    setChallengeToken(pending.challengeToken);
    setEmailHint(pending.emailHint || 'your email');
  }, [pending, navigate]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { code: '' },
    validationSchema: Yup.object({
      code: Yup.string()
        .matches(/^\d{6}$/, 'Enter the 6-digit code')
        .required('Enter the 6-digit code'),
    }),
    onSubmit: async (values) => {
      setError(null);
      setInfo(null);
      setSubmitting(true);
      try {
        // Always use the latest challenge from storage (resend / race-safe).
        const latest = getPendingEmailOtp();
        const token = latest?.challengeToken || challengeToken;
        if (!token) {
          throw new Error('Verification session expired. Please sign in again.');
        }
        if (token !== challengeToken) {
          setChallengeToken(token);
        }

        const result = await verifyEmailOtp(token, values.code);
        if (result.status === 'totp_required') {
          storePendingTotp({
            challengeToken: result.challengeToken,
            mode: 'verify',
            username: latest?.username || pending?.username,
            password: latest?.password || pending?.password,
          });
          navigate('/totp-verify');
          return;
        }
        if (result.status === 'totp_setup_required') {
          storePendingTotp({
            challengeToken: result.challengeToken,
            mode: 'setup',
            username: latest?.username || pending?.username,
            password: latest?.password || pending?.password,
          });
          navigate('/totp-setup');
          return;
        }
        dispatch(loginSuccess(result.authData));
        navigate('/dashboard');
      } catch (err: any) {
        setError(err?.message || 'Verification failed.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleResend = async () => {
    setError(null);
    setInfo(null);
    setResending(true);
    try {
      const latest = getPendingEmailOtp();
      const token = latest?.challengeToken || challengeToken;
      const result = await resendEmailOtp(token);
      setChallengeToken(result.challengeToken);
      if (result.emailHint) {
        setEmailHint(result.emailHint);
      }
      setInfo('A new verification code has been sent. Use the latest email only.');
    } catch (err: any) {
      setError(err?.message || 'Unable to resend code.');
    } finally {
      setResending(false);
    }
  };

  return (
    <React.Fragment>
      <div className="auth-page">
        <Container fluid className="p-0">
          <Row className="g-0">
            <Col lg={4} md={5} className="col-xxl-3">
              <div className="auth-full-page-content d-flex p-sm-5 p-4">
                <div className="w-100">
                  <div className="d-flex flex-column h-100">
                    <div className="mb-4 mb-md-5 text-center">
                      <Link to="/login" className="d-block auth-logo">
                        <img src={logo} alt="NGK Tours" height="28" />
                      </Link>
                    </div>
                    <div className="auth-content my-auto">
                      <div className="text-center">
                        <div className="avatar-lg mx-auto">
                          <div className="avatar-title rounded-circle bg-light">
                            <i className="bx bxs-envelope h2 mb-0 text-primary"></i>
                          </div>
                        </div>
                        <div className="p-2 mt-4">
                          <h4>Verify your email</h4>
                          <p className="text-muted mb-4">
                            Enter the 6-digit code sent to{' '}
                            <span className="fw-semibold">{emailHint}</span>
                          </p>
                        </div>
                      </div>

                      {error && <Alert color="danger">{error}</Alert>}
                      {info && <Alert color="success">{info}</Alert>}

                      <Form
                        className="custom-form"
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="mb-3">
                          <Label className="form-label">Verification code</Label>
                          <Input
                            name="code"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            placeholder="Enter 6-digit code"
                            value={validation.values.code}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                              validation.setFieldValue('code', digits);
                            }}
                            onBlur={validation.handleBlur}
                            invalid={
                              !!(validation.touched.code && validation.errors.code)
                            }
                          />
                          {validation.touched.code && validation.errors.code ? (
                            <FormFeedback type="invalid">
                              {validation.errors.code}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mt-3 d-grid">
                          <button
                            className="btn btn-primary btn-block"
                            type="submit"
                            disabled={submitting || !challengeToken}
                          >
                            {submitting ? 'Verifying...' : 'Verify & continue'}
                          </button>
                        </div>
                      </Form>

                      <div className="mt-4 text-center">
                        <p className="text-muted mb-0">
                          Didn&apos;t get the code?{' '}
                          <button
                            type="button"
                            className="btn btn-link p-0 align-baseline"
                            onClick={handleResend}
                            disabled={resending || !challengeToken}
                          >
                            {resending ? 'Sending...' : 'Resend code'}
                          </button>
                        </p>
                        <p className="text-muted mt-3 mb-0">
                          <Link
                            to="/login"
                            onClick={() => clearPendingEmailOtp()}
                          >
                            Back to login
                          </Link>
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 mt-md-5 text-center">
                      <p className="mb-0">
                        © {new Date().getFullYear()} NGK Tours
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <CarouselPage />
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EmailOtp;
